import React, { useState } from 'react';
import propTypes from 'prop-types';
import Header from 'components/CardShell/Header';
import SelectWithValidation from 'components/SelectWithValidation';
import DualButtons from 'components/DualButtons';
import InputWithValidation from 'components/InputWithValidation';
import { validateForm } from 'helpers/validateForm';
import styles from './styles.scss';

const SmartFilter = (props) => {
  const {
    title, pageTabs, cancel, editMode, submitForm, updateState, groupByMetadataKey,
  } = props;

  const {
    smartFilterWaitType,
    smartFilterWaitTimeMinutes,
    smartFilterPriority,
    smartFilterTimeWindowTimezone,
    smartFilterTimeWindowEarlyHour,
    smartFilterTimeWindowLateHour,
  } = props.formData;

  const [errors, setErrors] = useState(null);

  const earlyWindowValid = () => {
    const earlyHour = parseInt(smartFilterTimeWindowEarlyHour, 10);
    const lateHour = parseInt(smartFilterTimeWindowLateHour, 10);

    if (!earlyHour && lateHour) {
      return false;
    }

    if (lateHour) {
      return earlyHour
        && earlyHour < lateHour
        && earlyHour >= 0
        && earlyHour <= 23;
    }

    return true;
  };

  const lateWindowValid = () => {
    const earlyHour = parseInt(smartFilterTimeWindowEarlyHour, 10);
    const lateHour = parseInt(smartFilterTimeWindowLateHour, 10);

    if (!lateHour && earlyHour) {
      return false;
    }

    if (earlyHour) {
      return lateHour
        && lateHour > earlyHour
        && lateHour >= 0
        && lateHour <= 23;
    }

    return true;
  };

  const validationRules = {
    smart_filter_wait_type: [
      {
        name: 'selected',
        message: () => 'Wait type is required if wait time is set',
        isValid: () => !smartFilterWaitTimeMinutes || smartFilterWaitType,
      },
    ],
    smart_filter_wait_time_minutes: [
      {
        name: 'value',
        message: () => 'Wait time must be between 0 and 1440 minutes if wait type is set',
        isValid: () => !(!smartFilterWaitTimeMinutes && smartFilterWaitType)
                       || (smartFilterWaitType
                          && smartFilterWaitTimeMinutes
                          && smartFilterWaitTimeMinutes >= 0
                          && smartFilterWaitTimeMinutes <= 1440),
      },
    ],
    smart_filter_priority: [
      {
        name: 'value',
        message: () => 'Priority must be between 1 and 10000',
        isValid: () => !smartFilterPriority || (smartFilterPriority >= 1 && smartFilterPriority <= 10000),
      },
    ],
    smart_filter_time_window_early_hour: [
      {
        name: 'value',
        message: () => 'Earliest hour must be between 0 and 23 and earlier than latest hour',
        isValid: () => earlyWindowValid(),
      },
    ],
    smart_filter_time_window_late_hour: [
      {
        name: 'value',
        message: () => 'Latest hour must be between 0 and 23 and later than earliest hour',
        isValid: () => lateWindowValid(),
      },
    ],
  };

  const handleSubmit = () => {
    const formValidation = validateForm(
      validationRules,
      {
        smartFilterWaitType,
        smartFilterWaitTimeMinutes,
        smartFilterPriority,
        smartFilterTimeWindowEarlyHour,
        smartFilterTimeWindowLateHour,
      },
    );

    setErrors(formValidation);
    if (!formValidation.hasErrors) {
      submitForm();
    }
  };

  return (
    <div className={styles.cardSection}>
      <div className={styles.cardSectionNav}>
        {pageTabs()}
      </div>
      <div className={styles.cardSectionInner}>
        <Header>{title}</Header>
        {groupByMetadataKey ? (
          <>
            <p className={styles.innerContentTitle}>
              These settings allow you to group shipment notifications into single emails and constrain them to a specific time window.
              Also, priority can be used to prevent emails for a shipment if a higher priority filter has been triggered.
              (i.e. a priority 1 filter will prevent emails for a shipment if a priority 2 filter has been triggered but the email has not yet been sent).
            </p>
            <p className={styles.innerContentTitle}>
              Please see the user guide for more information on how to use these settings.
            </p>
            <div className={styles.inputGroup}>
              <SelectWithValidation
                label="Wait type"
                name="smart_filter_wait_type"
                defaultValue={null}
                value={smartFilterWaitType}
                options={[{ value: null, label: 'None' }, { value: 'Rolling', label: 'Rolling' }, { value: 'Fixed', label: 'Fixed' }]}
                onChange={(e) => { updateState({ smartFilterWaitType: e.value }); }}
                errors={errors}
              />
            </div>
            <div className={styles.inputGroup}>
              <InputWithValidation
                label="Wait time in minutes (0-1440)"
                placeholder="Wait time in minutes"
                type="text"
                name="smart_filter_wait_time_minutes"
                value={smartFilterWaitTimeMinutes}
                onChange={(e) => { updateState({ smartFilterWaitTimeMinutes: e.target.value }); }}
                errors={errors}
              />
            </div>
            <div className={styles.inputGroup}>
              <InputWithValidation
                label="Priority (1-10000)"
                placeholder="Priority"
                type="text"
                name="smart_filter_priority"
                value={smartFilterPriority}
                onChange={(e) => { updateState({ smartFilterPriority: e.target.value }); }}
                errors={errors}
              />
            </div>
            {/* TO BE ADDED IN FUTURE <div className={styles.inputGroup}>
          <SelectWithValidation
            label="Timezone"
            name="timeWindowOffset"
            defaultValue={null}
            options={[{ value: 'Europe/London', label: 'Europe/London' }]} Real data will be fetched from the backend
            value={smart_filter_time_window_timezone}
          />
        </div> */}
            <div className={styles.inputGroup}>
              <InputWithValidation
                label={`Earliest hour (0-23, ${smartFilterTimeWindowTimezone} timezone)`}
                placeholder="Earliest hour"
                type="text"
                name="smart_filter_time_window_early_hour"
                value={smartFilterTimeWindowEarlyHour}
                onChange={(e) => { updateState({ smartFilterTimeWindowEarlyHour: e.target.value }); }}
                errors={errors}
              />
            </div>
            <div className={styles.inputGroup}>
              <InputWithValidation
                label={`Latest hour (0-23, ${smartFilterTimeWindowTimezone} timezone)`}
                placeholder="Latest hour"
                type="text"
                name="smart_filter_time_window_late_hour"
                value={smartFilterTimeWindowLateHour}
                onChange={(e) => { updateState({ smartFilterTimeWindowLateHour: e.target.value }); }}
                errors={errors}
              />
            </div>
          </>
        ) : (
          <>
            <p className={styles.innerContentTitle}>
              Smart filter settings are an advanced feature that allows you to group shipment notifications into single emails and constrain them to a specific time window.
              To be used, a Grouping metadata key must be set.
            </p>
            <p className={styles.innerContentTitle}>
              Please contact support to enable this feature.
            </p>
          </>
        )}
      </div>
      <div className={styles.cardSectionButtons}>
        <DualButtons
          leftFunction={cancel}
          leftText="Cancel"
          rightFunction={handleSubmit}
          rightText={editMode ? 'Update Filter' : 'Create Filter'}
          theme="rightPink"
        />
      </div>
    </div>
  );
};

SmartFilter.propTypes = {
  pageTabs: propTypes.func.isRequired,
  cancel: propTypes.func.isRequired,
  editMode: propTypes.bool.isRequired,
  formData: propTypes.shape({
    smart_filter_wait_type: propTypes.string,
    smart_filter_wait_time_minutes: propTypes.number,
    smart_filter_priority: propTypes.number,
    smart_filter_time_window_offset: propTypes.string,
    smart_filter_time_window_early_hour: propTypes.number,
    smart_filter_time_window_late_hour: propTypes.number,
  }).isRequired,
};

export default SmartFilter;
